import { $t } from '@/i18n';
import { computed, onUnmounted, ref, shallowRef, watch, watchEffect } from '@vue/composition-api';
import { DBSource } from '../datasource-api/DBSource';
import { HTTPSource } from '../datasource-api/HTTPSource';
import { isEqual } from 'lodash';
import { utilsForUser } from '@tencent/wuji-utils';
import * as yaml from 'js-yaml';
import * as marked from 'marked';
import * as pako from 'pako';
import { AIPageContext } from '@/utils/global/api';
import { loadVue3Stuff } from './runtime-loader';
import useDepLoader from './use-dep-loader';
import { WUJI_DEPS_KEY } from '@utils/dependencyLoader/consts';
import { interceptWujiIfNeeded } from './check/setup';
import Wuji from '@/modules/ai-page/wujibase/wuji';
import WujiWorkflow from '@/modules/ai-page/wujibase/wuji-workflow';
import WujiUpload from '@/modules/ai-page/wujibase/wuji-upload';
export function useGogoRuntimeEnv(opts) {
    const { data, isLoadingData } = useData(opts);
    const wuji = new AIPageContext();
    Object.defineProperty(wuji, 'data', {
        get() {
            return data.value;
        },
    });
    const { loadDeps } = useDepLoader();
    let latestLoadExternalsPromiseAbortController = null;
    const latestLoadExternalsResult = shallowRef(null);
    onUnmounted(() => latestLoadExternalsPromiseAbortController?.abort());
    const vue3stuff = shallowRef();
    const isVue3StuffReady = computed(() => !!vue3stuff.value);
    loadVue3Stuff().then((c) => {
        vue3stuff.value = c;
    });
    watch(() => opts.value.externals, (n, o) => {
        if (isEqual(n, o) && latestLoadExternalsResult.value)
            return;
        latestLoadExternalsPromiseAbortController?.abort();
        const abortController = new AbortController();
        latestLoadExternalsPromiseAbortController = abortController;
        const promise = loadDeps(n, { signal: abortController.signal });
        latestLoadExternalsResult.value = null;
        promise.then((result) => {
            if (latestLoadExternalsPromiseAbortController !== abortController)
                return; // 过期
            latestLoadExternalsResult.value = result;
            latestLoadExternalsPromiseAbortController = null;
            console.log('[wuji-gogo] loading externals ok', n, result);
        }).catch((error) => {
            if (latestLoadExternalsPromiseAbortController !== abortController)
                return; // 过期
            latestLoadExternalsResult.value = { error: error.message };
            latestLoadExternalsPromiseAbortController = null;
            console.log('[wuji-gogo] loading externals err', n, error);
        });
    }, { immediate: true });
    const getGlobals = () => ({ wuji });
    function customRequire(moduleName) {
        if (moduleName in vue3stuff.value) {
            // 加载 ant-design-vue 和 tdesign-vue-next
            return vue3stuff.value[moduleName];
        }
        if (moduleName === 'axios')
            return utilsForUser.axios;
        if (moduleName === 'lodash')
            return utilsForUser.lodash;
        if (moduleName === 'dayjs')
            return utilsForUser.dayjs;
        if (moduleName === 'ajv')
            return utilsForUser.ajv;
        if (moduleName === 'crypto-js')
            return utilsForUser.crypto;
        if (moduleName === 'js-yaml')
            return yaml;
        if (moduleName === 'marked')
            return marked;
        if (moduleName === 'pako')
            return pako;
        if (moduleName === '@wujibase/wuji')
            return interceptWujiIfNeeded(new Wuji(), 'wuji');
        if (moduleName === '@wujibase/wuji-workflow')
            return interceptWujiIfNeeded(new WujiWorkflow(), 'wuji-workflow');
        if (moduleName === '@wujibase/wuji-upload')
            return interceptWujiIfNeeded(new WujiUpload(), 'wuji-upload');
        if (window[WUJI_DEPS_KEY][moduleName]) {
            return window[WUJI_DEPS_KEY][moduleName];
        }
        throw new Error($t('k_223445e', { arg_0: moduleName }));
    }
    watchEffect(() => {
        console.log('[wuji-gogo] latestLoadExternalsResult changed', latestLoadExternalsResult.value);
    });
    return {
        getGlobals,
        customRequire,
        isExternalsLoading: computed(() => isLoadingData.value || !latestLoadExternalsResult.value || !isVue3StuffReady.value),
        externalsLoadError: computed(() => latestLoadExternalsResult.value?.error),
    };
}
function useData(opts) {
    const { projectId, } = opts.value;
    const data = ref({});
    const isLoadingData = ref(false);
    const getData = async () => {
        const data = {};
        await Promise.all(opts.value.collectionRefs.map(async (collection) => {
            const { collectionId, type } = collection;
            let dataSource;
            if (type === 'db') {
                dataSource = new DBSource({
                    collectionId,
                    projectId,
                });
            }
            else {
                dataSource = new HTTPSource({
                    collectionId,
                    projectId,
                });
            }
            data[collectionId] = await dataSource.getMethods();
        }));
        return data;
    };
    watch(() => opts.value.collectionRefs, async (newValue, oldValue) => {
        if (isEqual(newValue, oldValue))
            return;
        isLoadingData.value = true;
        const dataSource = await getData();
        // 考虑await时间差问题，这里保护一下
        if (opts.value.collectionRefs !== newValue)
            return;
        data.value = dataSource;
        isLoadingData.value = false;
    }, { immediate: true });
    return {
        data,
        isLoadingData,
    };
}
